import { tr, useT } from '@/i18n';
import { formatCountdown } from '@/lib/format';
import styles from '@/pages/BridgePage.module.css';
import shared from '@/pages/pages.module.css';

import { AppCard } from './AppCard';
import { Button } from './Button';
import { AppIcon } from './AppIcons';
import { DownloadIcon } from './Icons';
import { NewBridgeLink } from './NewBridgeLink';
import item from './SecretView.module.css';
import type { SealedSecretProps } from './SealedSecret.types';

/**
 * What a link opens on: the bridge as it will look, its content still sealed. Nothing here is fetched or spent. The
 * Open secret button (a text) and Download (a file) are the person's own doing, and each is the moment one view is used and the
 * content is brought; a link scanner that runs the page's script clicks neither, so it finds no spent view and
 * nothing readable. The name and size of a file show only once it is open: they are inside the sealed header.
 */
export function SealedSecret({ preview, now, guide, onReveal, onDownload }: SealedSecretProps): React.JSX.Element {
  const t = useT();
  return (
    <AppCard guide={guide} stepKey="view" direction="back" testId="bridge-card" mode="secret">
      <div className={[styles.secretHead, styles.secretHeadSolo].join(' ')}>
        <h2 className={styles.secretTitle}>{t('sealedSecret.title')}</h2>
        <p className={styles.secretSub}>{t('sealedSecret.sub')}</p>
      </div>
      <div className={styles.secretList} data-testid="sealed">
        {preview.hasText ? (
          <article className={[item.secretItem, item.secretItemText].join(' ')} data-testid="sealed-text" aria-label={t('sealedSecret.textLabel')}>
            <pre className={item.secretText} aria-hidden="true">
              {'•'.repeat(16)}
            </pre>
            <div className={item.secretActions}>
              <Button type="button" size="small" onClick={onReveal} data-testid="reveal-secret">
                {t('sealedSecret.open')}
              </Button>
            </div>
          </article>
        ) : null}
        {preview.hasFile ? (
          <article className={[item.secretItem, item.sealedFile].join(' ')} data-testid="sealed-file" aria-label={t('sealedSecret.fileLabel')}>
            <div className={item.sealedFileRow}>
              <AppIcon kind="file" />
              <div className={item.sealedFileText}>
                <p className={item.sealedFileTitle}>{t('sealedSecret.fileTitle')}</p>
                <p className={item.sealedFileHint}>{t('sealedSecret.fileHint')}</p>
              </div>
              <Button type="button" size="small" icon={<DownloadIcon size={15} />} onClick={onDownload} aria-label={t('sealedSecret.downloadLabel')} data-testid="download-sealed">
                {t('common.download')}
              </Button>
            </div>
          </article>
        ) : null}
        {!preview.hasText && !preview.hasFile ? <p className={styles.empty}>{t('sealedSecret.empty')}</p> : null}
      </div>
      <div className={shared.secretFoot}>
        <div className={styles.sealedMeta}>
          {tr('common.expiresInCountdown', { strong: (children) => <strong data-testid="countdown">{children}</strong> }, { countdown: formatCountdown(preview.expiresAt, now) })}
        </div>
        <NewBridgeLink own />
      </div>
    </AppCard>
  );
}
